import React, {FC, useState} from "react"
import styled from "styled-components"

import {RenewalContact} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import GenericModal from "@irati/ui/src/GenericModal"
import {H2, Body} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {Firstname, Lastname, Email} from "@irati-web/fields/user"
import {Input, ValidationButton} from "@irati/ui/src/FormItem"
import Form from "@irati/ui/src/Form"
import salesService from "@irati/sales/services/sales"
import authentication from "@irati/auth"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

import mobileSchema from "../mobileSchema"

interface Props extends InjectIntl {
  signatureContact: RenewalContact
  onResendToNewContact: (email: string) => void
}

const FlexChild = styled.div`
  flex: 1 1 0;
`

const ResendRenewal: FC<Props> = ({signatureContact, onResendToNewContact, formatMessage}) => {
  const [firstName, setFirstName] = useState<string>()
  const [lastName, setLastName] = useState<string>()
  const [email, setEmail] = useState<string>()
  const [telephone, setTelephone] = useState<string>()
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)

  const resendContractToSameContact = () => {
    salesService.resendRenewalContract({businessId: authentication.business().uuid})
    setShowSuccessModal(true)
  }

  const resendContractToNewContact = () => {
    setLoad(
      salesService
        .resendRenewalContract({
          businessId: authentication.business().uuid,
          signatureContact: {
            firstName,
            lastName,
            email,
            telephone
          }
        })
        .then(() => {
          setShowEditModal(false)
          setShowSuccessModal(true)
          onResendToNewContact(email)
        })
        .catch(() => setShowError(true))
    )
  }

  return (
    <>
      <Flex marginTop={1} justifyContent="space-between" width="230px" position="relative" left="-25px">
        <Button tertiary onClick={() => setShowEditModal(true)}>
          <FormattedMessage id="admin.billing.renewals.resendContract.edit.button" />
        </Button>
        <Button tertiary onClick={resendContractToSameContact}>
          <FormattedMessage id="admin.billing.renewals.resendContract.resend.button" />
        </Button>
      </Flex>
      <GenericModal hideActions show={showEditModal} onHide={() => setShowEditModal(false)}>
        {showError && <Alert type={AlertTypes.error} title="admin.billing.renewals.resendContract.success.alert" />}
        <H2 marginBottom={2}>
          <FormattedMessage id="admin.billing.renewals.resendContract.edit.title" />
        </H2>
        <Body small marginBottom={1}>
          <FormattedMessage id="admin.billing.renewals.resendContract.edit.body" />
        </Body>
        <Form>
          <Flex justifyContent="flex-end" marginBottom={1} gap={2}>
            <FlexChild>
              <Firstname
                value={firstName}
                onChange={(value) => {
                  setShowError(false)
                  setFirstName(value)
                }}
              />
            </FlexChild>
            <FlexChild>
              <Lastname
                value={lastName}
                onChange={(value) => {
                  setShowError(false)
                  setLastName(value)
                }}
              />
            </FlexChild>
          </Flex>
          <Flex justifyContent="flex-end" marginBottom={2} gap={2}>
            <FlexChild>
              <Email
                value={email}
                onChange={(value) => {
                  setShowError(false)
                  setEmail(value)
                }}
              />
            </FlexChild>
            <FlexChild>
              <Input
                label={formatMessage({id: "admin.billing.renewals.resendContract.edit.fields.phone"})}
                value={telephone}
                name="telephone"
                onChange={(value) => {
                  setShowError(false)
                  setTelephone(value)
                }}
                validation={mobileSchema(formatMessage)}
              />
            </FlexChild>
          </Flex>
          <Flex justifyContent="flex-end">
            <ValidationButton primary onClick={resendContractToNewContact} load={load}>
              <FormattedMessage id="admin.billing.renewals.resendContract.edit.submit" />
            </ValidationButton>
          </Flex>
        </Form>
      </GenericModal>
      <GenericModal hideActions show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
        <H2 marginBottom={4}>
          <FormattedMessage id="admin.billing.renewals.resendContract.success.title" />
        </H2>
        <Body small marginBottom={2}>
          <FormattedMessage
            id="admin.billing.renewals.resendContract.success.body"
            values={{firstName: firstName || signatureContact.firstName, lastName: lastName || signatureContact.lastName}}
          />
        </Body>
        <Flex justifyContent="center">
          <img src={formatMessage({id: "admin.billing.renewals.resendContract.success.img"})} />
        </Flex>
      </GenericModal>
    </>
  )
}

export default injectIntl(ResendRenewal)
